import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Logo from '../images/hotel_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';

Modal.setAppElement('#root');

const Units = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [newUnit, setNewUnit] = useState({ 
    name: '',
    abbreviation: '',
    type: 'count' // count, weight, volume
  });
  const [editingUnit, setEditingUnit] = useState(null);
  const [currentUnit, setCurrentUnit] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFetchingUnits, setIsFetchingUnits] = useState(true);

  const unitsCollectionRef = collection(db, "units");

  useEffect(() => {
    fetchUnits();
  }, []);

  useEffect(() => {
    const filtered = units.filter((unit) =>
      unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (unit.abbreviation && unit.abbreviation.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredUnits(filtered);
  }, [searchTerm, units]);

  const fetchUnits = async () => {
    setIsFetchingUnits(true);
    try {
      const data = await getDocs(unitsCollectionRef);
      const unitList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setUnits(unitList);
    } catch (error) {
      setError("Failed to fetch units");
      setShowModal(true);
    } finally {
      setIsFetchingUnits(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUnit({ ...newUnit, [name]: value });
  };

  const handleAddUnit = async () => {
    if (!newUnit.name.trim()) {
      setError("Unit name cannot be empty");
      setShowModal(true);
      return;
    }
    
    if (!newUnit.abbreviation.trim()) {
      setError("Abbreviation cannot be empty");
      setShowModal(true);
      return;
    }

    setUpdate(true);
    try {
      await addDoc(unitsCollectionRef, {
        ...newUnit,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Unit added successfully");
      fetchUnits();
      setNewUnit({ 
        name: '',
        abbreviation: '',
        type: 'count'
      });
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add unit");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateUnit = async () => {
    setUpdate(true);
    try {
      const unitDoc = doc(db, "units", editingUnit.id);
      await updateDoc(unitDoc, newUnit);
      setSuccessMessage("Unit updated successfully");
      fetchUnits();
      setEditingUnit(null);
      setNewUnit({ 
        name: '',
        abbreviation: '',
        type: 'count'
      });
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update unit");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleEditUnit = (unitToEdit) => {
    setEditingUnit(unitToEdit);
    setNewUnit({ 
      name: unitToEdit.name,
      abbreviation: unitToEdit.abbreviation || '',
      type: unitToEdit.type || 'count'
    });
    setIsEditModalOpen(true);
  };

  const confirmDeleteUnit = (unitToDelete) => {
    setCurrentUnit(unitToDelete);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteUnit = async (id) => {
    setUpdate(true);
    try {
      const unitDoc = doc(db, "units", id);
      await deleteDoc(unitDoc);
      setSuccessMessage("Unit deleted successfully");
      fetchUnits();
    } catch (error) {
      setError("Failed to delete unit");
    } finally {
      setUpdate(false);
      setIsDeleteModalOpen(false);
      setShowModal(true);
    }
  };

  if (loading) return <Loader text={"LOADING"} />;
  if (!userData) return <Loader text={"READING"} />;

  const allowedRoles = ['Cashier', 'Admin', 'Supreme'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Manage Units</h2>
            <Link to="#" className="home-link" onClick={() => fetchUnits()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Unit</button>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by name or abbreviation"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {isFetchingUnits ? (
                  <p className="loading-text">Loading units ...</p>
                ) : filteredUnits.length === 0 ? (
                  <p className="loading-text">No units match your search.</p>
                ) : (
                  filteredUnits.map((unitItem) => (
                    <div key={unitItem.id} className="class-item">
                      <div className="column gap-1">
                        <p><strong>{unitItem.name}</strong> ({unitItem.abbreviation})<br /><span>{unitItem.type}</span></p>
                      </div>
                      <div className="row align-center justify-center gap-15">
                        <button className="list-btn" onClick={() => handleEditUnit(unitItem)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="list-btn delete-btn" onClick={() => confirmDeleteUnit(unitItem)}>
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      {/* Add Unit Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Unit"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Unit</h2>
        <form className="session-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Unit Name (e.g. Kilogram)"
            value={newUnit.name}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="abbreviation"
            className="input-field"
            placeholder="Abbreviation (e.g. kg)"
            value={newUnit.abbreviation}
            onChange={handleInputChange}
            required
          />
          <select
            name="type"
            className="input-field"
            value={newUnit.type}
            onChange={handleInputChange}
          >
            <option value="count">Count (e.g. pieces)</option>
            <option value="weight">Weight (e.g. grams, kilograms)</option>
            <option value="volume">Volume (e.g. liters, milliliters)</option>
          </select>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddUnit}>Add Unit</button>
          </div>
        </form>
      </Modal>

      {/* Edit Unit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          setNewUnit({ 
            name: '',
            abbreviation: '',
            type: 'count'
          });
          setEditingUnit(null);
        }}
        contentLabel="Edit Unit"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Unit</h2>
        <form className="session-form">
          <input
            type="text"
            name="name"
            className="input-field"
            placeholder="Unit Name (e.g. Kilogram)"
            value={newUnit.name}
            onChange={handleInputChange}
            required
          />
          <input
            type="text"
            name="abbreviation"
            className="input-field"
            placeholder="Abbreviation (e.g. kg)"
            value={newUnit.abbreviation}
            onChange={handleInputChange}
            required
          />
          <select
            name="type"
            className="input-field"
            value={newUnit.type}
            onChange={handleInputChange}
          >
            <option value="count">Count (e.g. pieces)</option>
            <option value="weight">Weight (e.g. grams, kilograms)</option>
            <option value="volume">Volume (e.g. liters, milliliters)</option>
          </select>
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
                setIsEditModalOpen(false);
                setNewUnit({ 
                  name: '',
                  abbreviation: '',
                  type: 'count'
                });
                setEditingUnit(null);
              }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateUnit}>Update Unit</button>
          </div>
        </form>
      </Modal>

      {/* Delete Unit Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        contentLabel="Delete Unit"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Delete Unit</h2>
        {currentUnit && (
          <div>
            <p>Are you sure you want to delete <strong>{currentUnit.name}</strong>?</p>
            <p>Products using this unit will need to be updated.</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsDeleteModalOpen(false)}>Cancel</button>
              <button className="login-button delete-btn" type="button" onClick={() => handleDeleteUnit(currentUnit.id)}>Delete</button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Units;